import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Plus, Pencil, Trash2, Save, Search, MapPin, ImageOff, Loader2, ArrowLeft,
  RefreshCw, Wand2, X, Copy, Check, AlertTriangle, Upload,
} from 'lucide-react';

const BLUE = '#1025B2';

// Same auth'd fetch pattern as Marketing.jsx (admin session bearer token).
async function callApi(path, body, method = 'GET') {
  const { supabase } = await import('../lib/supabase');
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(path, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
    body: body == null ? undefined : JSON.stringify(body),
  });
  const text = await res.text();
  // Not every failure is JSON: Vercel's own limits (e.g. a 413
  // FUNCTION_PAYLOAD_TOO_LARGE) return plain text, and blindly JSON.parsing
  // those turned a clear error into an unreadable parser exception.
  let data = {};
  if (text) { try { data = JSON.parse(text); } catch { data = { error: text.trim().split('\n')[0] }; } }
  if (!res.ok) {
    if (res.status === 413) throw new Error('That image is too large to upload. Try one under ~3MB.');
    throw new Error(data.error || `Request failed (${res.status})`);
  }
  return data;
}

// Heroes render 600px wide in email, so anything larger is wasted bytes — and
// Vercel hard-caps a serverless request body at ~4.5MB, which base64 blows past
// on a ~3.3MB photo. Downscale in the browser so ordinary camera/stock images
// just work, and the emails stay light.
const MAX_HERO_WIDTH = 1600;      // 2x the 600px render, plenty for retina
const RECODE_OVER_BYTES = 600 * 1024;
const MAX_UPLOAD_BYTES = 3.5 * 1024 * 1024; // stay clear of the platform cap

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(new Error('Could not read that file'));
    fr.readAsDataURL(file);
  });
}

async function prepareImageForUpload(file) {
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('That file does not look like an image'));
      i.src = objectUrl;
    });

    const width = img.naturalWidth || MAX_HERO_WIDTH;
    const height = img.naturalHeight || Math.round(width * 0.75);
    const scale = Math.min(1, MAX_HERO_WIDTH / width);
    const oversized = scale < 1;
    const heavy = file.size > RECODE_OVER_BYTES;

    // Small, already-web-sized, supported type → send as-is (no quality loss).
    if (!oversized && !heavy && ['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      return readFileAsDataUrl(file);
    }

    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    // Flatten any transparency onto the email's black background rather than
    // letting it turn black-on-black or white in some clients.
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);

    for (const quality of [0.82, 0.7, 0.55]) {
      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      if (dataUrl.length * 0.75 <= MAX_UPLOAD_BYTES) return dataUrl;
    }
    throw new Error('That image is too large even after resizing — try a smaller one.');
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

const emptyForm = () => ({
  destination_code: '', display_name: '', city_name: '', headline: '', intro: '',
  hero_image_url: '', hero_image_alt: '', airport_codes: '',
  blurbs: [{ title: '', body: '' }], active: false,
});

// Hero thumbnail. `exists` comes from the /public checklist: false => needs an
// image (amber flag); otherwise try to render it and fall back gracefully.
function Thumb({ url, exists }) {
  const [err, setErr] = useState(false);
  if (exists === false || !url || err) {
    return (
      <div className="flex h-12 w-[72px] shrink-0 items-center justify-center rounded-lg border border-amber-500/30 bg-amber-500/10" title="Hero image not in /public yet">
        <ImageOff className="h-4 w-4 text-amber-500" />
      </div>
    );
  }
  return <img src={url} alt="" onError={() => setErr(true)} className="h-12 w-[72px] shrink-0 rounded-lg border border-zinc-800 object-cover" />;
}

function Stat({ label, value, tone = 'default', onClick, active }) {
  const tones = {
    default: 'border-zinc-800 bg-zinc-950',
    emerald: 'border-emerald-500/20 bg-emerald-500/5',
    amber: 'border-amber-500/25 bg-amber-500/10',
  };
  const Cmp = onClick ? 'button' : 'div';
  return (
    <Cmp onClick={onClick}
      className={`rounded-xl border px-4 py-3 text-left transition-colors ${tones[tone]} ${onClick ? 'hover:border-zinc-600 cursor-pointer' : ''} ${active ? 'ring-1 ring-white/30' : ''}`}>
      <div className="text-2xl font-semibold tracking-tight text-white">{value}</div>
      <div className={`text-xs ${tone === 'amber' ? 'text-amber-400' : tone === 'emerald' ? 'text-emerald-400' : 'text-gray-500'}`}>{label}</div>
    </Cmp>
  );
}

export default function DestinationContent() {
  const [rows, setRows] = useState([]);
  const [imgMap, setImgMap] = useState({}); // code -> { filename, exists, source }
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all'); // all | active | staged | needs-image
  const [notice, setNotice] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('edit');
  const [preview, setPreview] = useState({ loading: false, html: '' });
  const [uploading, setUploading] = useState(null); // 'dialog' | destination_code
  const dialogFileRef = useRef(null);
  const rowFileRef = useRef(null);
  const pendingRowRef = useRef(null); // ref, not state — the change handler fires before a re-render

  const flash = (type, msg) => { setNotice({ type, msg }); window.clearTimeout(flash._t); flash._t = window.setTimeout(() => setNotice(null), 4500); };


  const loadImages = useCallback(async () => {
    try {
      const { statuses } = await callApi('/api/admin/destination-content/image-status');
      const m = {}; for (const s of statuses || []) m[s.code] = s;
      setImgMap(m);
    } catch { /* non-fatal: checklist just won't show */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try {
      const { destinations } = await callApi('/api/admin/destination-content');
      setRows(destinations || []);
      loadImages();
    } catch (e) { setLoadError(e.message); }
    finally { setLoading(false); }
  }, [loadImages]);
  useEffect(() => { load(); }, [load]);

  const imgOf = (code) => imgMap[code];
  const needsImage = useMemo(() => rows.filter((r) => imgOf(r.destination_code)?.exists === false).length, [rows, imgMap]);
  const counts = useMemo(() => ({ total: rows.length, active: rows.filter((r) => r.active).length }), [rows]);
  const missingList = useMemo(
    () => rows.filter((r) => imgOf(r.destination_code)?.exists === false)
      .map((r) => imgOf(r.destination_code).filename).filter(Boolean).sort(),
    [rows, imgMap],
  );

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === 'active' && !r.active) return false;
      if (filter === 'staged' && r.active) return false;
      if (filter === 'needs-image' && imgOf(r.destination_code)?.exists !== false) return false;
      if (!term) return true;
      return [r.destination_code, r.display_name, r.city_name, ...(r.airport_codes || [])].join(' ').toLowerCase().includes(term);
    });
  }, [rows, q, filter, imgMap]);

  const copyMissing = async () => {
    try { await navigator.clipboard.writeText(missingList.join('\n')); setCopied(true); setTimeout(() => setCopied(false), 2000); flash('ok', `Copied ${missingList.length} filenames`); }
    catch { flash('err', 'Clipboard unavailable'); }
  };

  const openEdit = (row) => {
    setIsNew(false); setTab('edit'); setPreview({ loading: false, html: '' });
    setEditing({
      ...row,
      airport_codes: (row.airport_codes || []).join(', '),
      blurbs: (row.blurbs && row.blurbs.length) ? row.blurbs.map((b) => ({ title: b.title || '', body: b.body || '' })) : [{ title: '', body: '' }],
      intro: row.intro || '', headline: row.headline || '', city_name: row.city_name || '', hero_image_alt: row.hero_image_alt || '',
    });
  };
  const openNew = () => { setIsNew(true); setTab('edit'); setPreview({ loading: false, html: '' }); setEditing(emptyForm()); };
  const close = () => setEditing(null);
  const set = (k, v) => setEditing((f) => ({ ...f, [k]: v }));
  const setBlurb = (i, k, v) => setEditing((f) => ({ ...f, blurbs: f.blurbs.map((b, idx) => (idx === i ? { ...b, [k]: v } : b)) }));
  const addBlurb = () => setEditing((f) => ({ ...f, blurbs: [...f.blurbs, { title: '', body: '' }] }));
  const rmBlurb = (i) => setEditing((f) => ({ ...f, blurbs: f.blurbs.filter((_, idx) => idx !== i) }));

  const toPayload = (f) => ({
    destination_code: f.destination_code, display_name: f.display_name, city_name: f.city_name,
    headline: f.headline, intro: f.intro, hero_image_url: f.hero_image_url, hero_image_alt: f.hero_image_alt,
    airport_codes: f.airport_codes, blurbs: (f.blurbs || []).filter((b) => b.title || b.body), active: f.active,
  });

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = toPayload(editing);
      if (isNew) { await callApi('/api/admin/destination-content', payload, 'POST'); flash('ok', `Created ${payload.destination_code}`); }
      else { await callApi(`/api/admin/destination-content/${editing.destination_code}`, payload, 'PUT'); flash('ok', `Saved ${editing.destination_code}`); }
      close(); await load();
    } catch (e) { flash('err', e.message); }
    finally { setSaving(false); }
  };

  const toggleActive = async (row) => {
    const next = !row.active;
    setRows((rs) => rs.map((r) => (r.destination_code === row.destination_code ? { ...r, active: next } : r)));
    try { await callApi(`/api/admin/destination-content/${row.destination_code}`, { active: next }, 'PUT'); }
    catch (e) { flash('err', e.message); setRows((rs) => rs.map((r) => (r.destination_code === row.destination_code ? { ...r, active: row.active } : r))); }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete ${row.display_name} (${row.destination_code})? This cannot be undone.`)) return;
    try { await callApi(`/api/admin/destination-content/${row.destination_code}`, null, 'DELETE'); flash('ok', `Deleted ${row.destination_code}`); await load(); }
    catch (e) { flash('err', e.message); }
  };

  const runPreview = async () => {
    if (!editing) return;
    setPreview({ loading: true, html: '' });
    try { const { html } = await callApi('/api/admin/destination-content/preview', toPayload(editing), 'POST'); setPreview({ loading: false, html }); }
    catch (e) { setPreview({ loading: false, html: '' }); flash('err', e.message); }
  };

  // Upload from the edit dialog: sets the form field, saved with the rest of the form.
  const onDialogFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setUploading('dialog');
    try {
      const data = await prepareImageForUpload(file);
      const { url } = await callApi('/api/admin/destination-content/upload-hero', { code: editing?.destination_code || '', data }, 'POST');
      set('hero_image_url', url);
      flash('ok', 'Image uploaded — save to apply');
    } catch (err) { flash('err', err.message); }
    finally { setUploading(null); }
  };

  // Quick upload straight from a list row: uploads AND persists in one step.
  const startRowUpload = (code) => { pendingRowRef.current = code; rowFileRef.current?.click(); };
  const onRowFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    const code = pendingRowRef.current; pendingRowRef.current = null;
    if (!file || !code) return;
    setUploading(code);
    try {
      const data = await prepareImageForUpload(file);
      await callApi('/api/admin/destination-content/upload-hero', { code, data, persist: true }, 'POST');
      flash('ok', `Hero uploaded for ${code}`);
      await load();
    } catch (err) { flash('err', err.message); }
    finally { setUploading(null); }
  };

  const filters = [['all', 'All'], ['active', 'Active'], ['staged', 'Staged'], ['needs-image', 'Needs image']];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Toast is PORTALED to body: page content lives inside
          <main className="relative z-10"> (Layout.jsx), which opens a stacking
          context — so any z-index here, however high, still renders beneath the
          fixed z-50 nav. Portaling escapes that context; top-24 also clears the
          nav bar visually. */}
      {notice && typeof document !== 'undefined' && createPortal(
        <div
          role="status"
          aria-live="polite"
          className={`fixed right-4 top-24 z-[9999] max-w-[min(28rem,calc(100vw-2rem))] rounded-lg px-4 py-2.5 text-sm shadow-xl ${notice.type === 'ok' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}
        >
          {notice.msg}
        </div>,
        document.body,
      )}

      {/* Shared hidden picker for per-row quick uploads */}
      <input ref={rowFileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onRowFile} />

      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        {/* Header */}
        <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
          <div>
            <a href="/Marketing" className="mb-3 inline-flex items-center gap-1.5 text-sm text-gray-400 transition-colors hover:text-gray-200">
              <ArrowLeft className="h-4 w-4" /> Marketing
            </a>
            <h1 className="text-4xl font-semibold tracking-tight">Destination Library</h1>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-gray-400">
              Curated content for the “{'{'}Destination{'}'} is calling” retargeting emails. A destination sends only when it’s <span className="text-gray-300">active</span> and has a working hero image — upload one straight from here, no redeploy needed.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="border-zinc-800 bg-transparent text-gray-300 hover:bg-zinc-900 hover:text-white" onClick={load} disabled={loading}>
              <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button size="sm" className="text-white" style={{ backgroundColor: BLUE }} onClick={openNew}>
              <Plus className="mr-1.5 h-4 w-4" /> New destination
            </Button>
          </div>
        </div>

        {/* Stat chips — the checklist at a glance */}
        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Destinations" value={counts.total} />
          <Stat label="Active" value={counts.active} tone="emerald" onClick={() => setFilter('active')} active={filter === 'active'} />
          <Stat label="Staged" value={Math.max(0, counts.total - counts.active)} onClick={() => setFilter('staged')} active={filter === 'staged'} />
          <Stat label="Need a hero image" value={needsImage} tone="amber" onClick={() => setFilter('needs-image')} active={filter === 'needs-image'} />
        </div>

        {needsImage > 0 && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {needsImage} destination{needsImage === 1 ? '' : 's'} still need a hero image — hit the <Upload className="mx-0.5 inline h-3 w-3" /> on any row to upload one instantly.
          </div>
        )}

        {/* Toolbar */}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code, city, airport…" className="border-zinc-800 bg-zinc-950 pl-9" />
          </div>
          <div className="flex overflow-hidden rounded-lg border border-zinc-800">
            {filters.map(([val, label]) => (
              <button key={val} onClick={() => setFilter(val)}
                className={`px-3 py-1.5 text-sm transition-colors ${filter === val ? 'text-white' : 'bg-transparent text-gray-400 hover:text-gray-200'}`}
                style={filter === val ? { backgroundColor: BLUE } : undefined}>
                {label}
              </button>
            ))}
          </div>
          {missingList.length > 0 && (
            <Button variant="outline" size="sm" className="border-amber-500/30 bg-transparent text-amber-300 hover:bg-amber-500/10" onClick={copyMissing}>
              {copied ? <Check className="mr-1.5 h-4 w-4" /> : <Copy className="mr-1.5 h-4 w-4" />}
              Copy {missingList.length} missing filenames
            </Button>
          )}
        </div>

        {/* List */}
        <div className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-20 text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
          ) : loadError ? (
            <div className="px-4 py-20 text-center text-sm text-red-400">
              Couldn’t load destinations: {loadError}
              <div className="mt-1 text-gray-400">Migrations 116–121 must be applied for this table to exist.</div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-20 text-center text-sm text-gray-400">No destinations match.</div>
          ) : (
            <div className="divide-y divide-zinc-900">
              {filtered.map((r) => {
                const img = imgOf(r.destination_code);
                return (
                  <div key={r.destination_code} className="flex items-center gap-3.5 px-4 py-3 transition-colors hover:bg-zinc-900/60">
                    <Thumb url={r.hero_image_url} exists={img?.exists} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-white">{r.display_name}</span>
                        <span className="rounded border border-zinc-700 px-1.5 py-px font-mono text-[10px] text-gray-400">{r.destination_code}</span>
                        {r.active
                          ? <span className="rounded-full bg-emerald-500/15 px-2 py-px text-[10px] font-normal text-emerald-400">Active</span>
                          : <span className="rounded-full bg-zinc-700/40 px-2 py-px text-[10px] font-normal text-gray-400">Staged</span>}
                        {img?.exists === false && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-px text-[10px] font-normal text-amber-400">
                            <ImageOff className="h-2.5 w-2.5" /> add {img.filename}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-1.5 truncate text-xs text-gray-500">
                        <MapPin className="h-3 w-3 shrink-0" /> {(r.airport_codes || [r.destination_code]).join(' · ')}
                        <span className="truncate text-gray-600">— {r.intro}</span>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {img?.exists !== true && (
                        <Button variant="ghost" size="icon" title={`Upload a hero image for ${r.display_name}`}
                          className="h-8 w-8 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300"
                          onClick={() => startRowUpload(r.destination_code)} disabled={uploading === r.destination_code}>
                          {uploading === r.destination_code ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                        </Button>
                      )}
                      <Switch checked={!!r.active} onCheckedChange={() => toggleActive(r)} aria-label="Active" />
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:bg-zinc-800 hover:text-white" onClick={() => openEdit(r)}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:bg-zinc-800 hover:text-red-400" onClick={() => del(r)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <p className="mt-3 text-center text-xs text-gray-600">
          Showing {filtered.length} of {rows.length} · uploads go straight to Storage; “Copy missing filenames” is there if you’d rather batch them into <span className="font-mono">/public</span>.
        </p>
      </div>

      {/* Edit / Create dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-hidden border-brand-800/45 bg-brand-950/95 backdrop-blur-xl text-white">
          <DialogHeader>
            <DialogTitle className="text-xl tracking-tight">{isNew ? 'New destination' : `Edit ${editing?.display_name || ''}`}</DialogTitle>
            <DialogDescription className="text-gray-400">
              {isNew ? 'Add a destination. It stays staged until you activate it.' : 'Changes apply to future sends only.'}
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === 'preview') runPreview(); }}>
              <TabsList className="mb-3 bg-zinc-900">
                <TabsTrigger value="edit">Edit</TabsTrigger>
                <TabsTrigger value="preview">Preview</TabsTrigger>
              </TabsList>

              <TabsContent value="edit" className="max-h-[62vh] space-y-4 overflow-y-auto pr-1">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-400">IATA code</Label>
                    <Input value={editing.destination_code} disabled={!isNew} onChange={(e) => set('destination_code', e.target.value.toUpperCase())} placeholder="OGG" className="border-zinc-800 bg-black uppercase" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-400">Airports (comma-separated)</Label>
                    <Input value={editing.airport_codes} onChange={(e) => set('airport_codes', e.target.value)} placeholder="JFK, LGA, EWR" className="border-zinc-800 bg-black" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-400">Display name</Label>
                    <Input value={editing.display_name} onChange={(e) => set('display_name', e.target.value)} placeholder="New York" className="border-zinc-800 bg-black" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-400">City name</Label>
                    <Input value={editing.city_name} onChange={(e) => set('city_name', e.target.value)} placeholder="New York" className="border-zinc-800 bg-black" />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-gray-400">Headline</Label>
                    <button type="button" className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-300" onClick={() => set('headline', `${editing.display_name || 'Somewhere new'} is calling.`)}>
                      <Wand2 className="h-3 w-3" /> use “{editing.display_name || '…'} is calling.”
                    </button>
                  </div>
                  <Input value={editing.headline} onChange={(e) => set('headline', e.target.value)} placeholder="New York is calling." className="border-zinc-800 bg-black" />
                </div>

                <div>
                  <Label className="text-xs text-gray-400">Intro paragraph</Label>
                  <Textarea value={editing.intro} onChange={(e) => set('intro', e.target.value)} rows={3} placeholder="The one you always come back to…" className="border-zinc-800 bg-black" />
                </div>

                <div>
                  <Label className="text-xs text-gray-400">Hero image</Label>
                  <div className="flex gap-2">
                    <Input value={editing.hero_image_url} onChange={(e) => set('hero_image_url', e.target.value)} placeholder="Upload an image, or paste an https:// URL" className="border-zinc-800 bg-black" />
                    <input ref={dialogFileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onDialogFile} />
                    <Button type="button" variant="outline" className="shrink-0 border-zinc-800 bg-transparent text-gray-300 hover:bg-zinc-900"
                      onClick={() => dialogFileRef.current?.click()} disabled={uploading === 'dialog'}>
                      {uploading === 'dialog' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />} Upload
                    </Button>
                  </div>
                  <p className="mt-1 text-[12.5px] text-gray-400">
                    Uploads land in Storage and are live immediately — no <span className="font-mono">/public</span> file, no redeploy. JPEG/PNG/WebP, up to 5MB.
                  </p>
                  {editing.hero_image_url && (
                    <img src={editing.hero_image_url} alt="" className="mt-2 h-32 w-full rounded-lg border border-zinc-800 object-cover" />
                  )}
                </div>

                <div>
                  <Label className="text-xs text-gray-400">Hero image alt</Label>
                  <Input value={editing.hero_image_alt} onChange={(e) => set('hero_image_alt', e.target.value)} placeholder="Manhattan skyline at dusk" className="border-zinc-800 bg-black" />
                </div>

                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <Label className="text-xs text-gray-400">Highlights ({editing.blurbs.length})</Label>
                    <Button type="button" variant="outline" size="sm" className="h-7 border-zinc-800 bg-transparent text-gray-300 hover:bg-zinc-900" onClick={addBlurb} disabled={editing.blurbs.length >= 6}>
                      <Plus className="mr-1 h-3 w-3" /> Add
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {editing.blurbs.map((b, i) => (
                      <div key={i} className="rounded-lg border border-zinc-800 bg-black/60 p-2">
                        <div className="flex items-center gap-2">
                          <Input value={b.title} onChange={(e) => setBlurb(i, 'title', e.target.value)} placeholder="Highlight title" className="h-8 border-zinc-800 bg-black" />
                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-gray-500 hover:text-red-400" onClick={() => rmBlurb(i)}><X className="h-4 w-4" /></Button>
                        </div>
                        <Textarea value={b.body} onChange={(e) => setBlurb(i, 'body', e.target.value)} rows={2} placeholder="One specific, evocative sentence." className="mt-2 border-zinc-800 bg-black" />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-black/60 px-3 py-2.5">
                  <Switch checked={!!editing.active} onCheckedChange={(v) => set('active', v)} />
                  <span className="text-sm">Active <span className="text-gray-400">— sends to qualifying searchers (needs a working hero image)</span></span>
                </div>
              </TabsContent>

              <TabsContent value="preview">
                <div className="max-h-[64vh] overflow-hidden rounded-lg border border-zinc-800">
                  {preview.loading ? (
                    <div className="flex items-center justify-center gap-2 py-24 text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /> Rendering…</div>
                  ) : preview.html ? (
                    <iframe title="Email preview" srcDoc={preview.html} className="h-[64vh] w-full bg-black" />
                  ) : (
                    <div className="py-24 text-center text-sm text-gray-400">No preview yet.</div>
                  )}
                </div>
                <p className="mt-2 text-center text-[13px] text-gray-400">Preview uses a sample searcher (dates + a savings offer) to show the full layout.</p>
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter className="mt-2">
            <Button variant="outline" className="border-zinc-800 bg-transparent text-gray-300 hover:bg-zinc-900" onClick={close} disabled={saving}>Cancel</Button>
            <Button className="text-white" style={{ backgroundColor: BLUE }} onClick={save} disabled={saving || !editing?.display_name || !editing?.destination_code}>
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
              {isNew ? 'Create' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
